// skills-frontend-development/src/Pages/Events/ThankYouManager.tsx
// This component goes inside the EventDetail page as a modal
// GF can record Thank Yous, view queue, and see summary

import React, { useEffect, useState } from 'react';
import ApiClient from '../../methods/api/apiClient';
import { toast } from 'react-toastify';
import { FaHandshake, FaTrophy, FaCheck, FaTimes, FaClock, FaTrash, FaEdit } from 'react-icons/fa';
import Swal from 'sweetalert2';

interface ThankYouManagerProps {
    eventId: string;
    groupId: string;
    attendees: any[];
    user: any;
    label?: string;
    onClose: () => void;
}

const ThankYouManager: React.FC<ThankYouManagerProps> = ({
    eventId,
    groupId,
    attendees,
    user,
    label = 'Thank Yous',
    onClose
}) => {
    const [activeTab, setActiveTab] = useState<'record' | 'queue' | 'summary'>('record');
    const [giverId, setGiverId] = useState('');
    const [receiverId, setReceiverId] = useState('');
    const [thankYouType, setThankYouType] = useState('');
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [queue, setQueue] = useState<any[]>([]);
    const [summary, setSummary] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editType, setEditType] = useState('');
    const [editNote, setEditNote] = useState('');
    const [editGiver, setEditGiver] = useState('');
    const [editReceiver, setEditReceiver] = useState('');
    const [sharedGroups, setSharedGroups] = useState<any[]>([]);
    const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([groupId]);
    const [allowGuestToGuest, setAllowGuestToGuest] = useState(false);

    // Get members list from attendees (checked-in or RSVP yes)
    // Separate registered members from guests
    const allAttendees = attendees.map((a: any) => {
        // cw: a guest who accepted an invitation has isGuest === false — they're a real member
        // having a userId alone doesn't make them a member (guests can have user accounts)
        const acceptedInvite = a.memberDetails?.isGuest === false;
        const isGuest = !acceptedInvite && (
            a.memberDetails?.isGuest === true ||
            a.type === 'guest' ||
            (!a.memberDetails?._id && !a.userId)
        );

        return {
            id: a.memberDetails?._id || a.userId || null,
            attendeeId: a._id || a.id,
            name: a.memberDetails?.fullName || a.fullName || 'Guest',
            email: a.email || a.memberDetails?.email,
            isCheckedIn: a.isPresent === true,
            rsvpYes: a.meetConfirm === 'Yes',
            rsvpNo: a.meetConfirm === 'No',
            isGuest
        };
    });

    // Registered members with user IDs
    const members = allAttendees.filter((m: any) => !m.isGuest && m.id);
    
    // Guests (no user account) - show individually but will be lumped in graph
    const guests = allAttendees.filter((m: any) => m.isGuest);
    
    // Combined list for dropdowns - include guests with guest_ prefix IDs, sorted alphabetically
    const selectableMembers = [
        ...members,
        ...guests.map((g: any) => ({ ...g, userId: g.id, id: `guest_${g.attendeeId}`, isGuest: true }))
    ].sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));

    useEffect(() => {
        // Fetch group setting for guest-to-guest TY
        ApiClient.get(`api/event-group/setting?groupId=${groupId}`).then((res: any) => {
            console.log('Guest-to-guest setting response:', JSON.stringify(res?.data?.[0]?.allowGuestToGuestThankYou), 'full:', JSON.stringify(res));
            if (res.success && res.data?.length > 0) {
                setAllowGuestToGuest(res.data[0].allowGuestToGuestThankYou || false);
            }
        }).catch((err: any) => { console.error('Failed to fetch group setting:', err); });
    }, [groupId]);

    useEffect(() => {
        if (activeTab === 'queue') loadQueue();
        if (activeTab === 'summary') loadSummary();
    }, [activeTab]);

    // cw: fetch queue count on mount so the tab label shows "Queue (N)"
    // without requiring the user to open the Queue tab first
    useEffect(() => {
        loadQueue();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // cw: fetch shared groups between giver and receiver (skip for guests)
    const fetchSharedGroups = async (giver: string, receiver: string) => {
        setSharedGroups([]);
        setSelectedGroupIds([groupId]);
        if (!giver || !receiver || giver.startsWith('guest_') || receiver.startsWith('guest_')) return;
        try {
            const res = await ApiClient.get('api/testimonial/shared-groups', { giverId: giver, receiverId: receiver });
            if (res.success && res.data && res.data.length > 1) {
                setSharedGroups(res.data);
                setSelectedGroupIds([groupId]);
            }
        } catch (err) {
            console.error('Failed to fetch shared groups:', err);
        }
    };

    const loadQueue = async () => {
        setLoading(true);
        try {
            const res = await ApiClient.get('api/testimonial/queue', { groupId, eventId });
            if (res.success) {
                setQueue(res.data || []);
            }
        } catch (err) {
            console.error('Failed to load queue:', err);
        }
        setLoading(false);
    };

    const loadSummary = async () => {
        setLoading(true);
        try {
            const res = await ApiClient.get('api/testimonial/event-summary', { groupId, eventId });
            if (res.success && res.data) {
                setSummary(res.data);
            }
        } catch (err) {
            console.error('Failed to load summary:', err);
        }
        setLoading(false);
    };

    const handleRecordThankYou = async () => {
        if (!giverId || !receiverId) {
            toast.error('Please select both a giver and receiver');
            return;
        }
        if (giverId === receiverId) {
            toast.error('Giver and receiver must be different people');
            return;
        }

        setSubmitting(true);
        try {
            const groupIdsToSend = selectedGroupIds.length > 0 ? selectedGroupIds : [groupId];
            const res = await ApiClient.post('api/testimonial/facilitator-give', {
                givenById: giverId,
                receivedById: receiverId,
                groupId,
                groupIds: groupIdsToSend,
                eventId,
                thankYouType: thankYouType || null,
                note: note || null
            });

            if (res.success) {
                toast.success(`${label.replace(/s$/, '')} submitted to queue.`);
                setGiverId('');
                setReceiverId('');
                setThankYouType('');
                setNote('');
                setSharedGroups([]);
                setSelectedGroupIds([groupId]);
                // Refresh queue count and summary
                loadQueue();
                if (activeTab === 'summary') loadSummary();
            }
        } catch (err: any) {
            toast.error(err.message || 'Failed to record');
        }
        setSubmitting(false);
    };

    const handleDecision = async (testimonialId: string, decision: string) => {
        const actionText = decision === 'accepted' ? 'Accepted' : decision === 'declined' ? 'Declined' : 'Deferred';

        // Optimistic update immediately — no confirmation popup
        if (decision === 'deferred') {
            setQueue(prev => prev.map(item => item._id === testimonialId ? { ...item, status: 'deferred', deferCount: (item.deferCount || 0) + 1 } : item));
        } else {
            setQueue(prev => prev.filter(item => item._id !== testimonialId));
        }
        toast.success(actionText);

        // Fire API call in background
        try {
            await ApiClient.put('api/testimonial/queue/decide', {
                testimonialId,
                decision,
                eventId
            });
        } catch (err: any) {
            toast.error(err.message || 'Failed to process — refreshing');
            loadQueue();
        }
    };

    const handleVoid = async (testimonialId: string) => {
        const result = await Swal.fire({
            title: `Remove this ${label.replace(/s$/, '')}?`,
            input: 'text',
            inputLabel: 'Reason (optional)',
            inputPlaceholder: 'e.g. Entered by mistake',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, remove',
            confirmButtonColor: '#EF4444'
        });

        if (!result.isConfirmed) return;

        try {
            const res = await ApiClient.put('api/testimonial/void', {
                testimonialId,
                voidReason: result.value || ''
            });
            if (res.success) {
                toast.success('Removed successfully');
                loadSummary();
            }
        } catch (err: any) {
            toast.error(err.message || 'Failed to remove');
        }
    };

    const findSelectableId = (pairId: string) => {
        if (!pairId) return '';
        const match = selectableMembers.find((m: any) =>
            m.id === pairId || m.id === `guest_${pairId}` || m.userId === pairId
        );
        return match?.id || pairId;
    };

    const startEdit = (pair: any) => {
        setEditingId(pair._id);
        setEditType(pair.thankYouType || '');
        setEditNote(pair.note || '');
        setEditGiver(findSelectableId(pair.givenBy));
        setEditReceiver(findSelectableId(pair.receivedBy));
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditType('');
        setEditNote('');
        setEditGiver('');
        setEditReceiver('');
    };

    const handleEdit = async () => {
        if (!editingId) return;
        try {
            const res = await ApiClient.put('api/testimonial/edit', {
                testimonialId: editingId,
                thankYouType: editType || null,
                note: editNote || null,
                givenById: editGiver || undefined,
                receivedById: editReceiver || undefined
            });
            if (res.success) {
                toast.success('Updated successfully');
                cancelEdit();
                loadSummary();
            }
        } catch (err: any) {
            toast.error(err.message || 'Failed to update');
        }
    };

    // Check if within 1-day correction window
    const isWithinCorrectionWindow = summary?.eventUpdatedAt
        ? (Date.now() - new Date(summary.eventUpdatedAt).getTime() < 24 * 60 * 60 * 1000)
        : true;

    const singularLabel = label.replace(/s$/, '');

    return (
        <div className="flex flex-col" style={{ maxHeight: '80vh' }}>
            {/* Tabs */}
            <div className="flex border-b mb-4">
                <button
                    onClick={() => setActiveTab('record')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === 'record' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                    Record {singularLabel}
                </button>
                <button
                    onClick={() => setActiveTab('queue')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === 'queue' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                    Queue {queue.length > 0 ? `(${queue.length})` : ''}
                </button>
                <button
                    onClick={() => setActiveTab('summary')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === 'summary' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                    Summary
                </button>
            </div>

            {/* Record Tab */}
            {activeTab === 'record' && (
                <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium mb-1">From (Giver)</label>
                            <select
                                className="w-full border rounded-lg p-2 text-sm"
                                value={giverId}
                                onChange={(e) => {
                                    const newGiver = e.target.value;
                                    setGiverId(newGiver);
                                    // Clear receiver if both would be guests (unless allowed)
                                    if (!allowGuestToGuest && newGiver?.startsWith('guest_') && receiverId?.startsWith('guest_')) {
                                        setReceiverId('');
                                        setSharedGroups([]);
                                        setSelectedGroupIds([groupId]);
                                    } else {
                                        fetchSharedGroups(newGiver, receiverId);
                                    }
                                }}
                            >
                                <option value="">Select member...</option>
                                {selectableMembers
                                    .filter((m: any) => allowGuestToGuest || !(receiverId?.startsWith('guest_') && m.isGuest))
                                    .map((m: any) => (
                                    <option key={m.id} value={m.id} disabled={m.rsvpNo}>
                                        {m.name}{m.isGuest ? ' (Guest)' : m.isCheckedIn ? ' ✓' : m.rsvpNo ? ' (not attending)' : !m.rsvpYes ? ' (pending RSVP)' : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">To (Receiver)</label>
                            <select
                                className="w-full border rounded-lg p-2 text-sm"
                                value={receiverId}
                                onChange={(e) => {
                                    const newReceiver = e.target.value;
                                    setReceiverId(newReceiver);
                                    fetchSharedGroups(giverId, newReceiver);
                                }}
                            >
                                <option value="">Select member...</option>
                                {selectableMembers
                                    .filter((m: any) => m.id !== giverId)
                                    .filter((m: any) => allowGuestToGuest || !(giverId?.startsWith('guest_') && m.isGuest))
                                    .map((m: any) => (
                                    <option key={m.id} value={m.id} disabled={m.rsvpNo}>
                                       {m.name}{m.isGuest ? ' (Guest)' : m.isCheckedIn ? ' ✓' : m.rsvpNo ? ' (not attending)' : !m.rsvpYes ? ' (pending RSVP)' : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {sharedGroups.length > 1 && (
                        <div>
                            <label className="block text-sm font-medium mb-1">Submit to shared groups</label>
                            <div className="space-y-1">
                                {sharedGroups.map((g: any) => {
                                    const isCurrentGroup = g._id === groupId;
                                    const isChecked = selectedGroupIds.includes(g._id);
                                    return (
                                        <label key={g._id} className="flex items-center gap-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                disabled={isCurrentGroup}
                                                onChange={() => {
                                                    if (isCurrentGroup) return;
                                                    setSelectedGroupIds(prev =>
                                                        prev.includes(g._id) ? prev.filter((id: string) => id !== g._id) : [...prev, g._id]
                                                    );
                                                }}
                                            />
                                            <span>{g.name}{isCurrentGroup ? ' (this event)' : ''}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium mb-1">Type (optional)</label>
                        <select
                            className="w-full border rounded-lg p-2 text-sm"
                            value={thankYouType}
                            onChange={(e) => setThankYouType(e.target.value)}
                        >
                            <option value="">Select type...</option>
                            <option value="referral">Referral</option>
                            <option value="introduction">Introduction</option>
                            <option value="business_outcome">Business Outcome</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Note (optional)</label>
                        <textarea
                            className="w-full border rounded-lg p-2 text-sm"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Brief description..."
                            maxLength={500}
                            rows={2}
                        />
                    </div>
                    <button
                        onClick={handleRecordThankYou}
                        disabled={!giverId || !receiverId || submitting}
                        className="bg-[#EF7A2B] text-white px-4 py-2 rounded-lg disabled:opacity-50 hover:bg-orange-600 text-sm"
                    >
                        {submitting ? 'Submitting...' : `Submit ${singularLabel} to Queue`}
                    </button>
                </div>
            )}

            {/* Queue Tab */}
            {activeTab === 'queue' && (
                <div className="overflow-y-auto" style={{ maxHeight: '60vh' }}>
                    {loading ? (
                        <div className="text-center py-4 text-gray-500">Loading...</div>
                    ) : queue.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <FaClock className="text-3xl mx-auto mb-2 opacity-50" />
                            <p>No pending {label.toLowerCase()} requests</p>
                            <p className="text-xs mt-1">Members can submit requests from their event page</p>
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                    <th className="text-left p-2">From</th>
                                    <th className="text-left p-2">To</th>
                                    <th className="text-left p-2">Type</th>
                                    <th className="text-center p-2">Status</th>
                                    <th className="text-center p-2">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {queue.map((item: any) => (
                                    <tr key={item._id} className="border-b hover:bg-gray-50">
                                        <td className="p-2">{item.giverName}</td>
                                        <td className="p-2">{item.receiverName}</td>
                                        <td className="p-2 capitalize">{item.thankYouType?.replace(/_/g, ' ') || '-'}</td>
                                        <td className="p-2 text-center">
                                            <span className={`px-2 py-1 rounded text-xs ${
                                                item.status === 'submitted' ? 'bg-yellow-100 text-yellow-700' :
                                                item.status === 'deferred' ? 'bg-blue-100 text-blue-700' :
                                                'bg-gray-100 text-gray-700'
                                            }`}>
                                                {item.status}{item.deferCount > 0 ? ` (${item.deferCount}x)` : ''}
                                            </span>
                                        </td>
                                        <td className="p-2 text-center">
                                            <div className="flex justify-center gap-1">
                                                <button
                                                    onClick={() => handleDecision(item._id, 'accepted')}
                                                    className="p-1 rounded bg-green-100 text-green-600 hover:bg-green-200"
                                                    title="Accept"
                                                >
                                                    <FaCheck />
                                                </button>
                                                <button
                                                    onClick={() => handleDecision(item._id, 'deferred')}
                                                    className="p-1 rounded bg-yellow-100 text-yellow-600 hover:bg-yellow-200"
                                                    title="Defer to next meeting"
                                                >
                                                    <FaClock />
                                                </button>
                                                <button
                                                    onClick={() => handleDecision(item._id, 'declined')}
                                                    className="p-1 rounded bg-red-100 text-red-600 hover:bg-red-200"
                                                    title="Decline"
                                                >
                                                    <FaTimes />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    <div className="mt-3 flex justify-end">
                        <button onClick={loadQueue} className="text-sm text-blue-600 hover:underline">Refresh</button>
                    </div>
                </div>
            )}

            {/* Summary Tab */}
            {activeTab === 'summary' && (
                <div className="overflow-y-auto" style={{ maxHeight: '60vh' }}>
                    {loading ? (
                        <div className="text-center py-4 text-gray-500">Loading...</div>
                    ) : !summary ? (
                        <div className="text-center py-8 text-gray-500">
                            <p>No data yet</p>
                        </div>
                    ) : (
                        <>
                            {/* Total count */}
                            <div className="bg-orange-50 rounded-lg p-4 mb-4 text-center">
                                <div className="text-3xl font-bold text-orange-600">{summary.totalCount}</div>
                                <div className="text-sm text-gray-600">{label} Recorded</div>
                            </div>

                            {/* Per-member breakdown */}
                            {summary.memberBreakdown?.length > 0 && (
                                <div className="mb-4">
                                    <h5 className="font-semibold text-sm mb-2">Member Breakdown</h5>
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="text-left p-2">Member</th>
                                                <th className="text-center p-2">Given</th>
                                                <th className="text-center p-2">Received</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {summary.memberBreakdown.map((m: any) => (
                                                <tr key={m._id} className="border-b">
                                                    <td className="p-2">{m.fullName}</td>
                                                    <td className="p-2 text-center text-blue-600 font-semibold">{m.given}</td>
                                                    <td className="p-2 text-center text-green-600 font-semibold">{m.received}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Pairs list */}
                            {summary.pairs?.length > 0 && (
                                <div>
                                    <h5 className="font-semibold text-sm mb-2">{singularLabel} Pairs</h5>
                                    <div className="space-y-2">
                                        {summary.pairs.map((p: any) => (
                                            <div key={p._id}>
                                                {editingId === p._id ? (
                                                    <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm space-y-2">
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <div>
                                                                <label className="block text-xs font-medium mb-1">From</label>
                                                                <select className="w-full border rounded p-1 text-sm" value={editGiver} onChange={(e) => {
                                                                    setEditGiver(e.target.value);
                                                                    if (e.target.value?.startsWith('guest_') && editReceiver?.startsWith('guest_')) {
                                                                        setEditReceiver('');
                                                                    }
                                                                }}>
                                                                    {selectableMembers
                                                                        .filter((m: any) => !(editReceiver?.startsWith('guest_') && m.isGuest))
                                                                        .map((m: any) => (
                                                                        <option key={m.id} value={m.id}>{m.name}{m.isGuest ? ' (Guest)' : ''}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                            <div>
                                                                <label className="block text-xs font-medium mb-1">To</label>
                                                                <select className="w-full border rounded p-1 text-sm" value={editReceiver} onChange={(e) => setEditReceiver(e.target.value)}>
                                                                    {selectableMembers
                                                                        .filter((m: any) => m.id !== editGiver)
                                                                        .filter((m: any) => !(editGiver?.startsWith('guest_') && m.isGuest))
                                                                        .map((m: any) => (
                                                                        <option key={m.id} value={m.id}>{m.name}{m.isGuest ? ' (Guest)' : ''}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs font-medium mb-1">Type</label>
                                                            <select className="w-full border rounded p-1 text-sm" value={editType} onChange={(e) => setEditType(e.target.value)}>
                                                                <option value="">None</option>
                                                                <option value="referral">Referral</option>
                                                                <option value="introduction">Introduction</option>
                                                                <option value="business_outcome">Business Outcome</option>
                                                                <option value="other">Other</option>
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs font-medium mb-1">Note</label>
                                                            <input type="text" className="w-full border rounded p-1 text-sm" value={editNote} onChange={(e) => setEditNote(e.target.value)} placeholder="Optional note..." />
                                                        </div>
                                                        <div className="flex gap-2 justify-end">
                                                            <button onClick={cancelEdit} className="px-3 py-1 text-sm border rounded text-gray-600 hover:bg-gray-100">Cancel</button>
                                                            <button onClick={handleEdit} className="px-3 py-1 text-sm bg-orange-500 text-white rounded hover:bg-orange-600">Save</button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center justify-between bg-gray-50 rounded p-2 text-sm">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-medium">{p.giverName}</span>
                                                            <span className="text-orange-500">→</span>
                                                            <span className="font-medium">{p.receiverName}</span>
                                                            {p.thankYouType && (
                                                                <span className="text-xs bg-blue-100 text-blue-700 px-1 rounded capitalize">{p.thankYouType.replace('_', ' ')}</span>
                                                            )}
                                                        </div>
                                                        {isWithinCorrectionWindow && (
                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    onClick={() => startEdit(p)}
                                                                    className="text-blue-400 hover:text-blue-600"
                                                                    title="Edit"
                                                                >
                                                                    <FaEdit className="text-xs" />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleVoid(p._id)}
                                                                    className="text-red-400 hover:text-red-600"
                                                                    title="Remove"
                                                                >
                                                                    <FaTrash className="text-xs" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {summary.totalCount === 0 && (
                                <div className="text-center py-4 text-gray-500">
                                    No {label.toLowerCase()} recorded for this event yet.
                                </div>
                            )}
                        </>
                    )}
                    <div className="mt-3 flex justify-end">
                        <button onClick={loadSummary} className="text-sm text-blue-600 hover:underline">Refresh</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ThankYouManager;